import React from 'react';
import { TextInput } from '@gafe/common-ui';
import merge from 'classnames';
import { Field, FormikValues, useFormikContext } from 'formik';
import { get } from 'lodash';

import { InputChangeEvent } from '@/pages/Lead/Create/types';
import { GoogleAddressDetails } from '@/types';
import { removeNonAlphabetic } from '@/utils/remove-non-alphabetic';
import { removeNonNumeric } from '@/utils/remove-non-numeric';

import { AddressLineWithSuggestionInput } from '../AddressLineWithSuggestionInput';

import { Labels, Names } from './constants';
import { Props } from './types';

import styles from './AddressInputs.module.scss';

export const AddressInputs = (props: Props) => {
  const { errors, touched, handleChange, validateField, setFieldTouched } =
    useFormikContext<FormikValues>();

  const componentStyle = props.style === 'row' ? styles.asRow : styles.asBlocks;
  const labels: Record<string, string> = Labels[props.labels];
  const names: Record<string, string> = Names[props.names || 'standard'];

  const onAddressSuggestionSelect = (addressDetails: GoogleAddressDetails) => {
    const { ...address } = addressDetails;

    Object.keys(address).forEach(key => {
      onChange({
        target: {
          name: names[key],
          value: addressDetails[key],
        },
      } as InputChangeEvent);
    });
  };

  const onChange = (event: InputChangeEvent) => {
    handleChange(event);
    setTimeout(() => {
      validateField(event.target.name);
      if (props.errorType === 'inlineFocused') {
        setFieldTouched(event.target.name);
      }
    }, 0);
  };

  const getFieldError = (fieldName: string): string => {
    return get(touched, fieldName) && get(errors, fieldName)
      ? (get(errors, fieldName) as string)
      : '';
  };

  return (
    <div className={componentStyle}>
      <Field
        aria-label="address_line_1"
        as={AddressLineWithSuggestionInput}
        classes={{ wrapper: styles.input_wrapper }}
        errorMessage={getFieldError(names.addressLine1)}
        hasError={Boolean(getFieldError(names.addressLine1))}
        inputContainerClassName={styles.big}
        labelText={labels.addressLine1}
        maxLength={255}
        name={names.addressLine1}
        onChange={onChange}
        onSuggestionSelect={onAddressSuggestionSelect}
        placeholder={props.placeholder}
        role="textbox"
        showRequired
        style={props.style}
        testId="address-line-1"
      />

      {props.hasAddress2 && (
        <Field
          aria-label="address_line_2"
          as={TextInput}
          classes={{
            wrapper: props.style === 'row' ? styles.input_wrapper_large : styles.input_wrapper,
          }}
          errorMessage={getFieldError(names.addressLine2)}
          hasError={Boolean(getFieldError(names.addressLine2))}
          labelText={labels.addressLine2}
          maxLength={255}
          name={names.addressLine2}
          onChange={onChange}
          onSuggestionSelect={onAddressSuggestionSelect}
          placeholder={props.placeholder2}
          role="textbox"
          testId="address-line-2-input"
        />
      )}

      <div className={merge(styles.inputGroup, styles.asColumns)}>
        <Field
          aria-label="city"
          as={TextInput}
          classes={{
            wrapper: props.style === 'row' ? styles.input_wrapper_narrow : styles.input_wrapper,
          }}
          errorMessage={getFieldError(names.city)}
          hasError={Boolean(getFieldError(names.city))}
          labelText={labels.city}
          maxLength={255}
          name={names.city}
          onChange={onChange}
          role="textbox"
          showRequired
          testId="city-input"
        />
        <Field
          aria-label="state"
          as={TextInput}
          classes={{
            wrapper: props.style === 'row' ? styles.input_wrapper_narrow : styles.input_wrapper,
          }}
          errorMessage={getFieldError(names.state)}
          hasError={Boolean(getFieldError(names.state))}
          labelText={labels.state}
          maxLength={2}
          name={names.state}
          onChange={event => {
            event.target.value = removeNonAlphabetic(event.target.value);
            event.target.value = event.target.value.toUpperCase();
            onChange(event);
          }}
          role="textbox"
          showRequired
          testId="state-input"
        />
        <Field
          aria-label="zip_code"
          as={TextInput}
          classes={{
            wrapper: props.style === 'row' ? styles.input_wrapper_narrow : styles.input_wrapper,
          }}
          errorMessage={getFieldError(names.zipCode)}
          hasError={Boolean(getFieldError(names.zipCode))}
          labelText={labels.zipCode}
          maxLength={5}
          name={names.zipCode}
          onChange={event => {
            event.target.value = removeNonNumeric(event.target.value);
            onChange(event);
          }}
          role="textbox"
          showRequired
          testId="zip-code-input"
        />
      </div>
    </div>
  );
};
